import type { LayoutElement } from '../../../../types';
import type { ToolPickupReadyEntry } from '../../../../features/simulation/toolPickupReady';
import { toolFoupsAtTime } from '../../../../features/simulation/toolPickupReady';
import { DEFAULT_TOOL_FOUP_CAPACITY } from '../../../../features/equipment/equipmentConstants';
import type { EquipmentPortPose, EquipmentPortVisual, Vec3 } from './equipmentPortTransfer';
import type { ElementPortOwnership } from './equipmentPortOwnership';
import {
  equipmentIdFromPortId,
  ownershipMatchesEquipmentId,
  resolveOwnedPortIndex,
} from './equipmentPortOwnership';
import { resolveFabChamberFoupPositions } from './FabProcessTool/fabToolLayout';
import type { FabToolLayout } from './FabProcessTool/fabToolLayout';

export type EquipmentFoupLocation = 'chamber' | 'loadPort';

export type EquipmentFoupSlotOccupant = {
  slotIndex: number;
  foupId: string;
  portId: string;
  portIndex: number;
  location: EquipmentFoupLocation;
  /** Equipment-local rest position. */
  position: Vec3;
};

export type EquipmentFoupSlotView = {
  capacity: number;
  occupied: number;
  chamber: EquipmentFoupSlotOccupant[];
  loadPortIndices: number[];
};

function dist3(a: Vec3, b: Vec3): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

function isNearInternal(
  position: Vec3,
  pose: EquipmentPortPose
): boolean {
  return dist3(position, pose.internal) < dist3(position, pose.loadPort) * 0.85;
}

/** FOUP capacity from layout metadata, instance, or load-port count. */
export function resolveEquipmentFoupCapacity(
  element: Pick<LayoutElement, 'customProperties' | 'metadata'> | null | undefined,
  portCount: number,
  instanceCapacity?: number | null
): number {
  const fromCustom = element?.customProperties?.foupCapacity;
  const candidates = [fromCustom, instanceCapacity, portCount, DEFAULT_TOOL_FOUP_CAPACITY];
  for (const value of candidates) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return Math.min(6, Math.max(1, Math.floor(value)));
    }
  }
  return Math.max(1, portCount);
}

function chamberPositionForSlot(
  slotIndex: number,
  portIndex: number,
  poses: EquipmentPortPose[],
  chamberSlotPositions: Vec3[]
): Vec3 {
  if (portIndex >= 0 && portIndex < poses.length) {
    return poses[portIndex]!.internal;
  }
  return chamberSlotPositions[slotIndex] ?? chamberSlotPositions[0] ?? [0, 0, 0];
}

function assignSlotIndex(
  portIndex: number | null,
  foupId: string,
  capacity: number,
  portCount: number,
  usedSlots: Set<number>
): number {
  if (portIndex != null && portIndex < capacity && portCount === capacity) {
    usedSlots.add(portIndex);
    return portIndex;
  }
  if (portIndex != null && portIndex < capacity && !usedSlots.has(portIndex)) {
    usedSlots.add(portIndex);
    return portIndex;
  }
  for (let i = 0; i < capacity; i += 1) {
    if (!usedSlots.has(i)) {
      usedSlots.add(i);
      return i;
    }
  }
  void foupId;
  return Math.min(capacity - 1, Math.max(0, portIndex ?? 0));
}

export type ResolveEquipmentFoupSlotsInput = {
  ownership: ElementPortOwnership;
  portCount: number;
  capacity: number;
  poses: EquipmentPortPose[];
  layout: FabToolLayout;
  portVisuals: EquipmentPortVisual[];
  source: 'live' | 'replay';
  simTimeS: number;
  toolPickupReady: readonly ToolPickupReadyEntry[];
  livePortFoupIds: Record<string, string[]> | null;
  replayPorts?: Record<string, { occupiedFoupIds?: string[]; inventory?: number }>;
  /** FOUP currently animating — remain visible via portVisuals, not chamber. */
  animatingFoupId?: string | null;
};

/**
 * Canonical equipment FOUP occupancy for 2D + 3D.
 * Socket `toolPickupReady` / port ledger is authoritative; visuals only decorate.
 */
export function resolveEquipmentFoupSlots(
  input: ResolveEquipmentFoupSlotsInput
): EquipmentFoupSlotView {
  const {
    ownership,
    portCount,
    capacity,
    poses,
    layout,
    portVisuals,
    source,
    simTimeS,
    toolPickupReady,
    livePortFoupIds,
    replayPorts,
    animatingFoupId,
  } = input;

  const chamberSlotPositions = resolveFabChamberFoupPositions(layout, capacity);
  const chamberByFoupId = new Map<string, EquipmentFoupSlotOccupant>();
  const loadPortIndices = new Set<number>();
  const usedSlots = new Set<number>();

  const addChamber = (foupId: string, portId: string, portIndex: number | null) => {
    if (!foupId || foupId === animatingFoupId) return;
    if (chamberByFoupId.has(foupId)) return;
    const slotIndex = assignSlotIndex(portIndex, foupId, capacity, portCount, usedSlots);
    const resolvedPortIndex = portIndex ?? slotIndex;
    chamberByFoupId.set(foupId, {
      slotIndex,
      foupId,
      portId,
      portIndex: resolvedPortIndex,
      location: 'chamber',
      position: chamberPositionForSlot(slotIndex, resolvedPortIndex, poses, chamberSlotPositions),
    });
  };

  if (source === 'live') {
    for (const entry of toolFoupsAtTime(toolPickupReady, simTimeS)) {
      if (!ownershipMatchesEquipmentId(ownership, entry.toolId)) continue;
      const portIndex = resolveOwnedPortIndex(ownership, entry.portId, portCount);
      addChamber(entry.foupId, entry.portId, portIndex);
    }
  }

  for (const visual of portVisuals) {
    if (visual.foupMode !== 'resting' || !visual.foupPosition || !visual.foupId) continue;
    const pose = poses[visual.portIndex];
    if (!pose) continue;
    if (isNearInternal(visual.foupPosition, pose)) {
      const portId =
        [...ownership.portIndexById.entries()].find(([, idx]) => idx === visual.portIndex)?.[0] ??
        '';
      addChamber(visual.foupId, portId, visual.portIndex);
    } else if (dist3(visual.foupPosition, pose.loadPort) < dist3(visual.foupPosition, pose.internal)) {
      loadPortIndices.add(visual.portIndex);
    }
  }

  const portFoupSource =
    source === 'live' && livePortFoupIds ? livePortFoupIds : null;

  for (const [portId, portIndex] of ownership.portIndexById) {
    const foupIds =
      portFoupSource?.[portId] ??
      replayPorts?.[portId]?.occupiedFoupIds ??
      [];
    if (foupIds.length === 0) continue;

    const visual = portVisuals[portIndex];
    const pose = poses[portIndex];
    const onDeck =
      visual?.foupMode === 'resting' &&
      visual.foupPosition &&
      pose &&
      dist3(visual.foupPosition, pose.loadPort) <= dist3(visual.foupPosition, pose.internal);

    for (const foupId of foupIds) {
      if (!foupId || foupId === animatingFoupId) continue;
      if (chamberByFoupId.has(foupId)) continue;
      if (onDeck || visual?.foupMode === 'flying') {
        loadPortIndices.add(portIndex);
        continue;
      }
      if (source === 'replay' && !visual?.foupPosition) {
        addChamber(foupId, portId, portIndex);
      }
    }
  }

  if (source === 'live' && livePortFoupIds) {
    for (const [portId, foupIds] of Object.entries(livePortFoupIds)) {
      if (
        foupIds.length === 0 ||
        !ownershipMatchesEquipmentId(ownership, equipmentIdFromPortId(portId))
      ) {
        continue;
      }
      const portIndex = resolveOwnedPortIndex(ownership, portId, portCount);
      if (portIndex == null) continue;
      const visual = portVisuals[portIndex];
      const pose = poses[portIndex];
      const deckRest =
        visual?.foupMode === 'resting' &&
        visual.foupPosition &&
        pose &&
        dist3(visual.foupPosition, pose.loadPort) < dist3(visual.foupPosition, pose.internal);
      if (deckRest) {
        loadPortIndices.add(portIndex);
        continue;
      }
      for (const foupId of foupIds) {
        if (foupId && foupId !== animatingFoupId && !chamberByFoupId.has(foupId)) {
          addChamber(foupId, portId, portIndex);
        }
      }
    }
  }

  const chamber = [...chamberByFoupId.values()].sort((a, b) => a.slotIndex - b.slotIndex);
  return {
    capacity,
    occupied: chamber.length + loadPortIndices.size,
    chamber,
    loadPortIndices: [...loadPortIndices].sort((a, b) => a - b),
  };
}

/** Whether the schematic load-port / slot index holds a FOUP (2D occupancy boxes). */
export function isEquipmentFoupSlotOccupied(
  portIndex: number,
  view: EquipmentFoupSlotView | undefined
): boolean {
  if (!view) return false;
  if (view.loadPortIndices.includes(portIndex)) return true;
  return view.chamber.some((s) => s.slotIndex === portIndex);
}
